import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Zap } from "lucide-react";

// The app's one action control, sitting in the navbar so the same shortcuts
// are a click away from every page. Opens on hover where hovering means
// something, and on click everywhere, so a tap is never fighting a hover.
const QuickActionsMenu = ({ actions, label = "Quick actions" }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);
  const hoverCapable = useRef(
    typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches
  );

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // A short grace period, so crossing the gap between button and menu does not
  // close it.
  const cancelClose = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={hoverCapable.current ? () => { cancelClose(); setOpen(true); } : undefined}
      onMouseLeave={hoverCapable.current ? scheduleClose : undefined}
    >
      <button
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-card transition hover:bg-brand-700"
      >
        <Zap size={15} strokeWidth={2.2} />
        <span className="hidden sm:inline">{label}</span>
        <ChevronDown
          size={15}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5 shadow-pop"
          >
            {actions.map(({ to, icon: Icon, label: actionLabel }) => (
              <Link
                key={to}
                to={to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-xl px-2.5 py-2 transition hover:bg-brand-50"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-gray-200/80 bg-gray-50 text-gray-500 transition group-hover:border-brand-200 group-hover:bg-white group-hover:text-brand-600">
                  <Icon size={15} strokeWidth={1.9} />
                </span>
                <span className="text-sm font-medium text-gray-700 group-hover:text-brand-700">
                  {actionLabel}
                </span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuickActionsMenu;
